import Google from '../assets/logo/Google_Pay.png'
import visa from '../assets/logo/Visa.png'
import apple from '../assets/logo/Apple_Pay.png'
import mastercard from '../assets/logo/Mastercard.png'

import { useState } from 'react'
import { Crown, CreditCard, MonitorSmartphone, ShieldCheck } from 'lucide-react'
import PricingToggle from '../components/PricingToggle'
import SectionHeading from '../components/SectionHeading'
import Reveal from '../components/Reveal'
import LifetimeOfferCard from '../components/LifetimeOfferCard'
import { useLanguage } from '../i18n/LanguageContext'
import { servicesContent } from '../i18n/content/services'

const buildFeatures = (c, beinLine) => c.features.map((line) => (line === '__BEIN__' ? beinLine : line));

// Matches the order of servicesContent[lang].lifetime.features
const LIFETIME_ICONS = [CreditCard, MonitorSmartphone, Crown, ShieldCheck];

const CheckIcon = ({ className = "text-success" }) => (
    <svg className={`${className} flex-shrink-0 mt-0.5`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20,6 9,17 4,12"></polyline>
    </svg>
);

const AdultIcon = ({ className = "text-primary" }) => (
    <svg className={`${className} flex-shrink-0 mt-0.5`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
);

const TvIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M9 20v-1h2v-2H4.616q-.691 0-1.153-.462T3 15.385v-9.77q0-.69.463-1.152T4.615 4h14.77q.69 0 1.152.463T21 5.616v9.769q0 .69-.463 1.153T19.385 17H13v2h2v1zm-4.384-4h14.769q.23 0 .423-.192t.192-.423v-9.77q0-.23-.192-.423T19.385 5H4.615q-.23 0-.423.192T4 5.616v9.769q0 .23.192.423t.423.192M4 16V5z"></path></svg>
);

const MonitorIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
);

const trackAddToCart = (contentName, value) => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'AddToCart', {
            content_name: contentName,
            currency: 'EUR',
            value,
        });
    }
};

const WHATSAPP_NUMBER = '4478466481143';

const buildWhatsAppLink = (message) =>
    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

const openWhatsAppOrder = (message) => {
    window.open(buildWhatsAppLink(message), '_blank', 'noopener,noreferrer');
};

const ShieldIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
);

function PricingCard({ plan, planKey, pricingMode, iconVariant, hasAdultLine, beinLine, highlighted, c }) {
    const features = buildFeatures(c, beinLine);
    const [priceMain, priceRest] = plan.price.split(',');

    return (
        <div
            className={`relative rounded-2xl p-6 sm:p-7 h-full flex flex-col transition-all duration-300 ${
                highlighted
                    ? 'text-white shadow-xl lg:scale-[1.03] lg:-translate-y-2 z-10'
                    : 'bg-surface border border-line shadow-sm hover:shadow-md hover:border-primary/30 hover:-translate-y-1'
            }`}
            style={highlighted ? { background: 'linear-gradient(160deg, #0a1a5f 0%, #002f9c 55%, #0d47ff 130%)' } : undefined}
        >
            {highlighted && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap gold-gradient text-ink text-[11px] font-black uppercase tracking-wide px-4 py-1.5 rounded-full shadow-md">
                    {plan.badge}
                </span>
            )}

            <h3 className={`text-sm font-extrabold uppercase tracking-wider mb-4 ${highlighted ? 'text-white/85' : 'text-ink'}`}>
                {plan.title}
            </h3>

            <div key={pricingMode} className="price-fade-in flex items-end gap-1">
                <span className={`text-4xl sm:text-[2.75rem] font-black leading-none ${highlighted ? 'text-white' : 'text-ink'}`}>
                    {priceMain}
                </span>
                <span className={`text-lg sm:text-xl font-bold leading-none mb-0.5 ${highlighted ? 'text-white/70' : 'text-ink-muted'}`}>
                    ,{priceRest}
                </span>
            </div>
            <p key={`${pricingMode}-period`} className={`price-fade-in text-xs mb-4 mt-1.5 ${highlighted ? 'text-white/60' : 'text-ink-muted'}`}>
                {c.paymentOnce} &middot; {plan.period}
            </p>

            <div className={`inline-flex items-center gap-2 w-fit px-3 py-1.5 rounded-lg text-xs font-semibold mb-5 border ${
                highlighted ? 'bg-white/10 border-white/10 text-white/80' : 'bg-surface-alt border-line text-ink-muted'
            }`}>
                {iconVariant === 'tv' ? (
                    <span className="flex items-center gap-1">
                        <TvIcon />
                        {pricingMode === "multi" && <TvIcon />}
                    </span>
                ) : (
                    <span className="flex items-center gap-1">
                        <MonitorIcon />
                        {pricingMode === "multi" && <MonitorIcon />}
                    </span>
                )}
                {plan.screens}
            </div>

            <div className={`h-px w-full mb-5 ${highlighted ? 'bg-white/10' : 'bg-line'}`}></div>

            <div className="space-y-2.5 mb-7 flex-grow">
                {hasAdultLine && (
                    <div className="flex items-start gap-2.5">
                        <AdultIcon className={highlighted ? 'text-secondary-bright' : 'text-primary'} />
                        <span className={`text-[13px] leading-snug ${highlighted ? 'text-white/80' : 'text-ink-muted'}`}>{c.adultLine}</span>
                    </div>
                )}
                {features.map((text) => (
                    <div className="flex items-start gap-2.5" key={text}>
                        <CheckIcon className={highlighted ? 'text-secondary-bright' : 'text-success'} />
                        <span className={`text-[13px] leading-snug ${highlighted ? 'text-white/80' : 'text-ink-muted'}`}>{text}</span>
                    </div>
                ))}
            </div>

            <button
                onClick={() => {
                    trackAddToCart(plan.title, plan.value);
                    openWhatsAppOrder(
                        c.whatsappOrderMessage(plan.title, plan.price, plan.period, plan.screens)
                    );
                }}
                className={`btn-glow w-full py-3.5 rounded-full font-bold transition-all duration-300 hover:-translate-y-0.5 mt-auto ${
                    highlighted
                        ? 'btn-glow-gold gold-gradient text-ink'
                        : 'bg-ink hover:bg-ink/90 text-white'
                }`}
            >
                <span>{c.orderButton}</span>
            </button>

            <p className={`text-xs text-center mt-4 mb-3 flex items-center justify-center gap-1.5 ${highlighted ? 'text-white/50' : 'text-ink-muted'}`}>
                <ShieldIcon />
                {c.securePayment}
            </p>

            <div className={`flex items-center justify-center gap-3 px-3 py-2 rounded-lg ${highlighted ? 'bg-white' : 'bg-surface-alt'}`}>
                <img src={visa} alt="Visa" className="h-4 w-auto" />
                <img src={mastercard} alt="Mastercard" className="h-4 w-auto" />
                <img src={Google} alt="Google Pay" className="h-4 w-auto" />
                <img src={apple} alt="Apple Pay" className="h-4 w-auto" />
            </div>
        </div>
    );
}

export default function Services(){
    const [pricingMode, setPricingMode] = useState("standard");
    const { lang } = useLanguage();
    const c = servicesContent[lang];

    const pricingConfig = {
        standard: {
            basic: { price: 29, screens: c.screensStandard.basic, period: c.periodsStandard.basic, title: c.planTitles.basic },
            premium: { price: 39, screens: c.screensStandard.premium, period: c.periodsStandard.premium, title: c.planTitles.premium },
            gold: { price: 49, screens: c.screensStandard.gold, period: c.periodsStandard.gold, title: c.planTitles.gold, badge: c.badge }
        },
        multi: {
            basic: { price: 69, screens: c.screensMulti.basic, period: c.periodsMulti.basic, title: c.planTitles.basic },
            premium: { price: 89, screens: c.screensMulti.premium, period: c.periodsMulti.premium, title: c.planTitles.premium },
            gold: { price: 99, screens: c.screensMulti.gold, period: c.periodsMulti.gold, title: c.planTitles.gold, badge: c.badge }
        }
    };

    const pricing = {
        basic: {
            ...pricingConfig[pricingMode].basic,
            value: pricingConfig[pricingMode].basic.price,
            price: `${pricingConfig[pricingMode].basic.price},00 €`,
        },
        premium: {
            ...pricingConfig[pricingMode].premium,
            value: pricingConfig[pricingMode].premium.price,
            price: `${pricingConfig[pricingMode].premium.price},00 €`,
        },
        gold: {
            ...pricingConfig[pricingMode].gold,
            value: pricingConfig[pricingMode].gold.price,
            price: `${pricingConfig[pricingMode].gold.price},00 €`,
        },
    };

    return (
        <section className="section2" id="services" style={{backgroundColor: 'var(--bg-color)'}}>
            <div className="main-content">
                <Reveal className="mb-8 flex flex-col items-center gap-6">
                    <SectionHeading eyebrow={c.eyebrow} title={c.title} align="center" />
                    <PricingToggle pricingMode={pricingMode} setPricingMode={setPricingMode} />
                </Reveal>

                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-6 pb-5 pt-6">
                    <Reveal>
                        <PricingCard
                            plan={pricing.basic}
                            planKey="basic"
                            pricingMode={pricingMode}
                            iconVariant="tv"
                            hasAdultLine={false}
                            beinLine={c.beinStandard}
                            highlighted={false}
                            c={c}
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <PricingCard
                            plan={pricing.gold}
                            planKey="gold"
                            pricingMode={pricingMode}
                            iconVariant="monitor"
                            hasAdultLine={true}
                            beinLine={c.beinPremium}
                            highlighted={true}
                            c={c}
                        />
                    </Reveal>
                    <Reveal delay={160} className="last-service-tablet">
                        <PricingCard
                            plan={pricing.premium}
                            planKey="premium"
                            pricingMode={pricingMode}
                            iconVariant="monitor"
                            hasAdultLine={true}
                            beinLine={c.beinPremium}
                            highlighted={false}
                            c={c}
                        />
                    </Reveal>
                </div>

                {/* FORFAIT À VIE — premium lifetime offer card */}
                <div className="mb-8 mt-8">
                    <LifetimeOfferCard
                        badgeText={c.lifetime.badgeText}
                        title={c.lifetime.title}
                        description={c.lifetime.description}
                        features={c.lifetime.features.map((f, i) => ({ ...f, icon: LIFETIME_ICONS[i] }))}
                        priceNote={c.lifetime.priceNote}
                        ctaLabel={c.lifetime.ctaLabel}
                        securityLabel={c.lifetime.securityLabel}
                        onOrder={() => {
                            trackAddToCart(c.lifetime.title, 159);
                            openWhatsAppOrder(c.lifetime.whatsappMessage);
                        }}
                    />
                </div>

                {/* Besoin d'autre chose ? — slim dark support bar */}
                <Reveal className="mb-5">
                    <div
                        className="rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4"
                        style={{ background: 'linear-gradient(135deg, #16224f, #060b1c)' }}
                    >
                        <div className="text-center sm:text-left">
                            <h3 className="text-white font-bold text-base sm:text-lg">{c.supportTitle}</h3>
                            <p className="text-white/50 text-sm mt-1">{c.supportBody}</p>
                        </div>

                        <a
                            href="https://wa.me/4478466481143"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-white text-ink px-6 py-3 rounded-xl font-bold text-sm whitespace-nowrap transition-all duration-300 hover:bg-white/90 flex-shrink-0"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                            </svg>
                            {c.supportCta}
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
